import { useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { useConsent } from "../consent/ConsentContext";

export function CookieBanner() {
  const { t } = useI18n();
  const { decided, prefs, acceptAll, necessaryOnly, savePreferences } = useConsent();
  const [manage, setManage] = useState(false);
  const [analytics, setAnalytics] = useState(prefs.analytics);
  const [marketing, setMarketing] = useState(prefs.marketing);

  if (decided && !manage) return null;

  const onSave = () => {
    savePreferences({ analytics, marketing });
    setManage(false);
  };

  return (
    <>
      {/* Floating banner */}
      {!decided && !manage && (
        <div
          className="fixed inset-x-3 md:inset-x-auto md:right-6 bottom-3 md:bottom-6 z-[60] max-w-2xl mx-auto md:mx-0 md:ml-auto"
          role="dialog"
          aria-live="polite"
          aria-label="Cookies"
          data-testid="cookie-banner"
        >
          <div className="border border-border bg-card/95 backdrop-blur-md rounded-md shadow-xl p-5 md:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.22em] font-bold text-accent">{t.cookieBanner.title}</p>
                <p className="mt-2 text-sm text-foreground/85 leading-relaxed max-w-xl">
                  {t.cookieBanner.body}{" "}
                  <Link to="/cookies" className="underline underline-offset-4 hover:text-accent">
                    {t.cookieBanner.learnMore}
                  </Link>
                </p>
              </div>
            </div>
            <div className="mt-5 flex flex-col sm:flex-row gap-2.5">
              <button
                type="button"
                onClick={necessaryOnly}
                data-testid="cookie-necessary-only"
                className="btn-ghost flex-1 py-2.5 text-sm"
              >
                {t.cookieBanner.necessaryOnly}
              </button>
              <button
                type="button"
                onClick={() => setManage(true)}
                data-testid="cookie-preferences"
                className="btn-ghost flex-1 py-2.5 text-sm"
              >
                {t.cookieBanner.preferences}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-accept-all"
                className="btn-primary flex-1 py-2.5 text-sm"
              >
                {t.cookieBanner.acceptAll}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage preferences modal */}
      {manage && (
        <div className="fixed inset-0 z-[70] grid place-items-center p-4 bg-black/50 backdrop-blur-sm" role="dialog" aria-modal="true" data-testid="cookie-manage">
          <div className="bg-card border border-border rounded-md max-w-xl w-full p-6 md:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.22em] font-bold text-accent">{t.cookieBanner.manageTitle}</p>
                <h3 className="mt-2 text-2xl text-foreground tracking-tight">{t.cookieBanner.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t.cookieBanner.manageBody}</p>
              </div>
              <button
                type="button"
                onClick={() => setManage(false)}
                aria-label={t.cookieBanner.close}
                className="h-9 w-9 grid place-items-center rounded-md border border-border hover:border-accent transition-colors"
                data-testid="cookie-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <ul className="mt-6 divide-y divide-border border-y border-border">
              <li className="py-5 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="font-medium text-foreground">{t.cookieBanner.necessary}</p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{t.cookieBanner.necessaryDesc}</p>
                </div>
                <span className="text-xs uppercase tracking-[0.18em] font-bold text-accent">Required</span>
              </li>
              <li className="py-5 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="font-medium text-foreground">{t.cookieBanner.analytics}</p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{t.cookieBanner.analyticsDesc}</p>
                </div>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={analytics}
                    onChange={(e) => setAnalytics(e.target.checked)}
                    className="sr-only peer"
                    data-testid="cookie-toggle-analytics"
                  />
                  <span className="relative w-11 h-6 bg-secondary border border-border rounded-full peer-checked:bg-accent transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-card after:border after:border-border after:h-4 after:w-4 after:rounded-full after:transition-transform peer-checked:after:translate-x-5" />
                </label>
              </li>
              <li className="py-5 flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="font-medium text-foreground">{t.cookieBanner.marketing}</p>
                  <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{t.cookieBanner.marketingDesc}</p>
                </div>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                    className="sr-only peer"
                    data-testid="cookie-toggle-marketing"
                  />
                  <span className="relative w-11 h-6 bg-secondary border border-border rounded-full peer-checked:bg-accent transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-card after:border after:border-border after:h-4 after:w-4 after:rounded-full after:transition-transform peer-checked:after:translate-x-5" />
                </label>
              </li>
            </ul>

            <div className="mt-6 flex flex-col sm:flex-row gap-2.5">
              <button type="button" onClick={necessaryOnly} className="btn-ghost flex-1 text-sm py-2.5" data-testid="cookie-manage-necessary">
                {t.cookieBanner.necessaryOnly}
              </button>
              <button type="button" onClick={onSave} className="btn-primary flex-1 text-sm py-2.5" data-testid="cookie-manage-save">
                {t.cookieBanner.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CookieBanner;
